/**
 * Every host-bridge call returns a discriminated result rather than throwing a
 * raw Office error upward (contract 3 of docs/plans/41).
 *
 * Office.js failures arrive as callback objects with opaque codes; letting them
 * propagate means every call site invents its own handling and the author sees
 * "1002" instead of a sentence. `unsupported` is deliberately distinct from
 * `failed`: it is not an error, it is the signal to take the degradation path.
 */
export type HostResult<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly reason: 'unsupported';
      /** The requirement set that was missing, e.g. `PowerPointApi 1.4`. */
      readonly requirement: string;
      readonly message: string;
    }
  | {
      readonly ok: false;
      readonly reason: 'failed';
      readonly message: string;
      readonly code?: string;
    }
  | { readonly ok: false; readonly reason: 'cancelled'; readonly message: string };

export function ok<T>(value: T): HostResult<T> {
  return { ok: true, value };
}

export function unsupported<T>(requirement: string, degradation: string): HostResult<T> {
  return {
    ok: false,
    reason: 'unsupported',
    requirement,
    message: `This version of PowerPoint does not provide ${requirement}. ${degradation}`,
  };
}

export function failed<T>(message: string, code?: string): HostResult<T> {
  return { ok: false, reason: 'failed', message, ...(code ? { code } : {}) };
}

export function cancelled<T>(message = 'The operation was cancelled.'): HostResult<T> {
  return { ok: false, reason: 'cancelled', message };
}

/** Narrows to the success value, or throws. Only for call sites that cannot degrade. */
export function unwrap<T>(result: HostResult<T>): T {
  if (result.ok) return result.value;
  throw new Error(result.message);
}
